const MAX_MEETING_REQUESTS = 4;
const MEETING_REQUEST_DEADLINE_MS = 20000;
const RETRY_DELAYS_MS = [1000, 2000, 4000];

const waitForDelay = (delay) => {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
};

const getRetryAfterMilliseconds = (response = {}) => {
  const retryAfterHeader = response.headers && (response.headers['retry-after'] || response.headers['Retry-After']);
  const retryAfterSeconds = Number(retryAfterHeader || response.data?.retryAfterSeconds || 0);

  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 0;
};

const getRetryDelay = (attempt, response) => {
  const baseDelay = RETRY_DELAYS_MS[attempt - 1] || RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
  const jitteredDelay = Math.round(baseDelay * (0.8 + Math.random() * 0.4));

  return Math.max(jitteredDelay, getRetryAfterMilliseconds(response));
};

const resolveMeetingOperation = async ({ requestMeeting, isContextCurrent, onRetry }) => {
  const deadline = Date.now() + MEETING_REQUEST_DEADLINE_MS;

  for (let attempt = 1; attempt <= MAX_MEETING_REQUESTS; attempt += 1) {
    if (!isContextCurrent()) {
      return { isStale: true };
    }

    const response = await requestMeeting();
    if (!isContextCurrent()) {
      return { isStale: true };
    }

    const isPending = response && response.status === 202 && response.data && response.data.pending;

    if (!isPending) {
      return { response };
    }

    if (attempt === MAX_MEETING_REQUESTS) {
      return { isPending: true, response };
    }

    const retryDelay = getRetryDelay(attempt, response);
    if (Date.now() + retryDelay > deadline) {
      return { isPending: true, response };
    }

    if (onRetry) {
      onRetry({ attempt, retryDelay, response });
    }

    await waitForDelay(retryDelay);
  }

  return { isPending: true };
};

export const resolveMeetingWithRetry = async ({
  contextKey,
  inFlightOperations,
  requestMeeting,
  isContextCurrent,
  onRetry,
}) => {
  const existingOperation = inFlightOperations.get(contextKey);
  if (existingOperation) {
    const result = await existingOperation;

    return { ...result, isCoalesced: true };
  }

  const operation = resolveMeetingOperation({ requestMeeting, isContextCurrent, onRetry });
  inFlightOperations.set(contextKey, operation);

  try {
    const result = await operation;

    return { ...result, isCoalesced: false };
  } finally {
    if (inFlightOperations.get(contextKey) === operation) {
      inFlightOperations.delete(contextKey);
    }
  }
};
